import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminListEvents, adminCreateEvent, adminUpdateEvent, adminDeleteEvent, adminEventRsvps } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";
import { EVENT_TYPES, formatEventDate } from "@/lib/events";
import { Plus, Loader2, Save, X, Trash2, Eye, EyeOff, CalendarDays, Users, Edit } from "lucide-react";

const blank = {
  title: "", type: "katha", host: "dharmartha", organizer_name: "", description: "",
  mode: "in_person", venue: "", city: "", online_url: "", start: "", end: "",
  image_url: "", register_url: "", contact: "", rsvp_enabled: true, status: "open",
};

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | event object
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [rsvpFor, setRsvpFor] = useState(null);
  const [rsvps, setRsvps] = useState([]);

  const load = () => { setLoading(true); adminListEvents().then(setEvents).finally(() => setLoading(false)); };
  useEffect(load, []);

  const openNew = () => { setForm(blank); setEditing("new"); };
  const openEdit = (e) => { setForm({ ...blank, ...e }); setEditing(e); };

  const save = async (ev) => {
    ev.preventDefault();
    if (!form.title.trim() || !form.start.trim()) { toast("Title and date/time are required"); return; }
    setBusy(true);
    try {
      if (editing === "new") await adminCreateEvent(form);
      else await adminUpdateEvent(editing.id, form);
      toast("Saved"); setEditing(null); load();
    } catch (err) { toast("Could not save", { description: err?.response?.data?.detail || "Try again." }); }
    finally { setBusy(false); }
  };
  const del = async (e) => { if (!window.confirm("Delete this event?")) return; await adminDeleteEvent(e.id); setEvents((x) => x.filter((y) => y.id !== e.id)); };
  const toggleHide = async (e) => { const updated = await adminUpdateEvent(e.id, { status: e.status === "hidden" ? "open" : "hidden" }); setEvents((x) => x.map((y) => y.id === e.id ? updated : y)); };
  const viewRsvps = async (e) => { setRsvpFor(e); setRsvps(await adminEventRsvps(e.id)); };

  const field = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";

  return (
    <div data-testid="admin-events">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif-latin text-3xl text-ink">Events</h1>
          <p className="mt-1 text-sm text-ink-3">Kathas, yagyas, festival celebrations & talks — by Dharmartha or the community.</p>
        </div>
        <button onClick={openNew} data-testid="admin-event-new" className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-4 py-2 text-sm font-medium text-white"><Plus className="h-4 w-4" /> New event</button>
      </div>

      {editing && (
        <form onSubmit={save} data-testid="admin-event-form" className="mt-6 border border-line bg-surface p-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-title" placeholder="Event title" className={field} />
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="event-type" className={field}>
              {EVENT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.en}</option>)}
            </select>
            <select value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} data-testid="event-host" className={field}>
              <option value="dharmartha">Hosted by Dharmartha</option>
              <option value="community">Hosted by community / others</option>
            </select>
            <input value={form.organizer_name} onChange={(e) => setForm({ ...form, organizer_name: e.target.value })} placeholder="Organizer name (for community events)" className={field} />
            <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} data-testid="event-mode" className={field}>
              <option value="in_person">In person</option>
              <option value="online">Online</option>
              <option value="hybrid">Hybrid (in person + online)</option>
            </select>
            <div className="flex items-center gap-2 text-sm text-ink-2">
              <label className="flex items-center gap-2"><input type="checkbox" checked={form.rsvp_enabled} onChange={(e) => setForm({ ...form, rsvp_enabled: e.target.checked })} /> Allow on-site RSVP</label>
            </div>
            <label className="text-xs text-ink-3">Start<input type="datetime-local" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="event-start" className={field} /></label>
            <label className="text-xs text-ink-3">End (optional)<input type="datetime-local" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className={field} /></label>
            {form.mode !== "online" && <input value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} placeholder="Venue" className={field} />}
            {form.mode !== "online" && <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" className={field} />}
            {form.mode !== "in_person" && <input value={form.online_url} onChange={(e) => setForm({ ...form, online_url: e.target.value })} placeholder="Online / stream URL" className={`${field} sm:col-span-2`} />}
            <input value={form.register_url} onChange={(e) => setForm({ ...form, register_url: e.target.value })} placeholder="External Register / Ticket URL (optional)" className={field} />
            <input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="Contact (phone / email)" className={field} />
          </div>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} placeholder="Description" className={`${field} mt-3`} />
          <div className="mt-3">
            <p className="mb-1 text-xs text-ink-3">Cover image</p>
            <ImageUploader value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} />
          </div>
          <div className="mt-4 flex gap-2">
            <button type="submit" disabled={busy} data-testid="event-save" className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-4 py-2 text-sm font-medium text-white disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>
            <button type="button" onClick={() => setEditing(null)} className="border border-line px-4 py-2 text-sm text-ink-2"><X className="h-4 w-4" /></button>
          </div>
        </form>
      )}

      {/* RSVP viewer */}
      {rsvpFor && (
        <div className="mt-6 border border-line bg-surface p-5">
          <div className="flex items-center justify-between">
            <p className="font-serif-latin text-lg text-ink">RSVPs — {rsvpFor.title} ({rsvps.length})</p>
            <button onClick={() => setRsvpFor(null)} className="text-ink-2"><X className="h-4 w-4" /></button>
          </div>
          {rsvps.length === 0 ? <p className="mt-3 text-sm text-ink-3">No RSVPs yet.</p> : (
            <div className="mt-3 divide-y divide-line text-sm">
              {rsvps.map((r, i) => <div key={i} className="flex flex-wrap gap-x-4 py-2 text-ink-2"><span className="font-medium text-ink">{r.name}</span>{r.email && <span>{r.email}</span>}{r.phone && <span>{r.phone}</span>}</div>)}
            </div>
          )}
        </div>
      )}

      <div className="mt-6 divide-y divide-line border-y border-line">
        {loading ? <div className="flex items-center gap-2 py-10 text-ink-3"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          : events.length === 0 ? <p className="py-10 text-center text-ink-3">No events yet. Create the first one.</p>
          : events.map((e) => (
            <div key={e.id} data-testid={`admin-event-${e.id}`} className={`flex items-center gap-4 py-4 ${e.status === "hidden" ? "opacity-50" : ""}`}>
              <CalendarDays className="h-4 w-4 shrink-0 text-ink-3" />
              <div className="min-w-0 flex-1">
                <p className="font-serif-latin text-ink">{e.title}</p>
                <p className="text-xs text-ink-3"><span className="uppercase tracking-wider text-gold">{e.type}</span> · {formatEventDate(e.start)} · {e.host === "dharmartha" ? "Dharmartha" : (e.organizer_name || "community")} · {e.mode}{e.rsvp_count ? ` · ${e.rsvp_count} RSVPs` : ""}{e.status === "hidden" ? " · HIDDEN" : ""}</p>
              </div>
              <button onClick={() => viewRsvps(e)} data-testid={`admin-event-rsvps-${e.id}`} className="p-2 text-ink-2 hover:text-saffron" title="RSVPs"><Users className="h-4 w-4" /></button>
              <button onClick={() => openEdit(e)} data-testid={`admin-event-edit-${e.id}`} className="p-2 text-ink-2 hover:text-saffron" title="Edit"><Edit className="h-4 w-4" /></button>
              <button onClick={() => toggleHide(e)} data-testid={`admin-event-toggle-${e.id}`} className="p-2 text-ink-2 hover:text-saffron" title={e.status === "hidden" ? "Unhide" : "Hide"}>{e.status === "hidden" ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
              <button onClick={() => del(e)} data-testid={`admin-event-delete-${e.id}`} className="p-2 text-red-600" title="Delete"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
      </div>
    </div>
  );
}
